import BuildConfig from '$extensionSrc/buildConfig'
import { XSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { StageDef } from '../automationMachineInterfaces'
import { TWITTER_AUTOMATION_STAGE_STATUSES } from './twitterAutomationStageStatuses'
import * as twitterUrls from './twitterUrls'
import { NO_TIMEOUT } from '../../consts/stageMachineConsts'
import { xRateLimitFaqUrl } from '../../consts/urlConsts'
import { sendAlertNotification } from '../../utils/communication'
import {
  MONTHS,
  OLDER_THAN_OPTION,
  getOlderThanMonthYear,
} from '../../utils/dateUtils'
import {
  PLATFORM,
  StageType,
  TwitterRecommendationKey,
} from '../../utils/enums'
import {
  getStoredMachineOverlayStatus,
  setStoredMachineOverlayStatus,
} from '../automationStageMachine'
import {
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import { evaluateXpath, getNearestElementMatch } from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { getElement, getInitialValueBySettingKey } from '../utils'

const getCurrentValueFn = () => () => ''
const getIsFulfilledFn = () => () => true
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

export type XDeletePostsState = {
  count: number // total count of posts deleted
  message: string
  result?: {
    title: string
    detail?: string
    helper?: {
      text: string
      link: string
    }
  }
  numPosts?: number // num posts that user has. gathered from profile
  mostRecentPostDate?: string
  username?: string
}

async function getTimeline(): Promise<HTMLElement> {
  const timeline = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    () =>
      evaluateXpath<HTMLElement>(
        document,
        '//div[@aria-label="Home timeline"]',
      )[0],
  )

  return timeline
}

async function waitForFirstPostToLoad(
  timeline: HTMLElement,
  username: string,
  timeMs: number,
): Promise<HTMLElement> {
  const firstPost = await getWaitForElementToAppearPromise<HTMLElement>(
    timeline,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        `./descendant::article[descendant::span[` +
          `  contains(text(), "@${username}") or ` +
          `  contains(text(), "You reposted")]]`,
      )[0],
    timeMs,
  )

  return firstPost
}

// Gets datetimes of posts, replies, and quote posts by the username specified
async function getVisibleUserPostDates(username: string): Promise<string[]> {
  const timeline = await getTimeline()

  if (timeline) {
    await waitForFirstPostToLoad(timeline, username, 5000)

    const postDates = evaluateXpath<HTMLTimeElement>(
      timeline,
      `//article[descendant::span[contains(text(),"@${username}")]]/descendant::time[1]`,
    ).map((x) => x.dateTime)

    return postDates
  }
  return []
}

// Gets all reposts on the page using post date as a reference
async function getVisibleRepostDates(): Promise<string[]> {
  const timeline = await getTimeline()

  if (timeline) {
    const postDates = evaluateXpath<HTMLTimeElement>(
      timeline,
      `//article[descendant::span[contains(text(), "You reposted")]]/descendant::time[1]`,
    ).map((x) => x.dateTime)

    return postDates
  }
  return []
}

async function hasVisiblePostsOrReposts(username: string): Promise<boolean> {
  let postsAndReposts
  const timeline = await getTimeline()

  if (timeline) {
    // wait 5 seconds for a post to load before gathering posts
    const firstPost = await waitForFirstPostToLoad(timeline, username, 5000)

    if (firstPost) {
      postsAndReposts = evaluateXpath<HTMLAnchorElement>(
        timeline,
        `./descendant::article[descendant::span[` +
          `  contains(text(), "@${username}") or ` +
          `  contains(text(), "You reposted")]]`,
      )
    }
  }

  remoteLog.debug(
    `hasVisiblePostsOrReposts: found ${postsAndReposts?.length} posts and reposts on profile`,
  )
  return postsAndReposts?.length > 0
}

async function scrollToEarliestPostAfter(
  selectedDateOption: OLDER_THAN_OPTION,
  month: number,
  year: number,
  username: string,
): Promise<{ foundPosts: boolean; foundNonRepost: boolean | undefined }> {
  const selectedDate = new Date(year, month)
  const postsVisible = await hasVisiblePostsOrReposts(username)

  let numTryNoChange = 0
  let prevScrollHeight = window.scrollY
  let foundNonRepost

  if (postsVisible) {
    // Do not need to scroll if option is to delete all and there are posts
    if (selectedDateOption === OLDER_THAN_OPTION.NOW) {
      return { foundPosts: true, foundNonRepost }
    }

    while (numTryNoChange < 5) {
      const postDates = await getVisibleUserPostDates(username)
      postDates.sort((a, b) => Date.parse(a) - Date.parse(b))
      const earliestPostTime = postDates[0]

      if (earliestPostTime) {
        foundNonRepost = true

        if (Date.parse(earliestPostTime) < selectedDate.getTime()) {
          return { foundPosts: true, foundNonRepost }
        }
      } else {
        foundNonRepost = false
      }

      window.scrollBy(0, window.innerHeight)
      await wait(500)

      // Check if no change based on scroll position
      if (window.scrollY === prevScrollHeight) {
        numTryNoChange += 1
      } else {
        numTryNoChange = 0
      }

      prevScrollHeight = window.scrollY
    }
  }

  return {
    foundPosts: false,
    foundNonRepost,
  }
}

async function getAllPostsLaterThan(
  selectedDateOption: OLDER_THAN_OPTION,
  month: number,
  year: number,
  username: string,
): Promise<HTMLElement[]> {
  const timeline = await getTimeline()
  const posts = []

  if (timeline) {
    const postDates = await getVisibleUserPostDates(username)
    const olderThanTimestamp = new Date(year, month).getTime()

    const postDatesOlderThan = postDates.filter(
      (dateTime) => Date.parse(dateTime) < olderThanTimestamp,
    )

    for (const postDate of postDatesOlderThan) {
      const post = await getElement<HTMLElement>(
        timeline,
        `//article[descendant::time[@dateTime="${postDate}"]]`,
      )
      posts.push(post)
      remoteLog.debug(
        `getAllPostsLaterThan: adding post to delete from date ${postDate}`,
      )
    }

    const repostsDates = await getVisibleRepostDates()

    for (const repostDate of repostsDates) {
      const repost = await getElement<HTMLElement>(
        timeline,
        `//article[descendant::time[@dateTime="${repostDate}"]]`,
      )

      if (selectedDateOption !== OLDER_THAN_OPTION.NOW) {
        const nearestUserPostTimeElement =
          await getNearestElementMatch<HTMLTimeElement>(
            repost,
            `article[descendant::span[contains(text(),"@${username}")]]/descendant::time[1]`,
            'preceding',
          )

        if (
          nearestUserPostTimeElement?.dateTime &&
          Date.parse(nearestUserPostTimeElement.dateTime) < olderThanTimestamp
        ) {
          posts.push(repost)
          remoteLog.debug(
            `getAllPostsLaterThan: adding repost to delete from date ${repostDate}`,
          )
        }
      } else {
        posts.push(repost)
        remoteLog.debug(
          `getAllPostsLaterThan: adding repost to delete from date ${repostDate}`,
        )
      }
    }
  }

  return posts
}

async function isRepost(post: HTMLElement): Promise<boolean> {
  const repostLabel = await getWaitForElementToAppearPromise<HTMLSpanElement>(
    post,
    () =>
      evaluateXpath<HTMLSpanElement>(
        post,
        'descendant::span[text()="You reposted"]',
      )[0],
    300,
  )

  if (repostLabel) {
    return true
  }

  return false
}

async function undoRepost(post: HTMLElement, isDry: boolean): Promise<void> {
  if (!post) {
    return
  }

  const repostButton = await getWaitForElementToAppearPromise(
    post,
    () =>
      evaluateXpath<HTMLElement>(
        post,
        'descendant::button[contains(@aria-label, "Reposted")]',
      )[0],
    3000,
  )

  repostButton?.click()

  const unRepostMenuItem = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    () =>
      evaluateXpath<HTMLElement>(
        document,
        '//div[@role="menuitem"]//*[contains(text(), "Undo repost")]',
      )[0],
    3000,
  )

  if (unRepostMenuItem && !isDry) {
    unRepostMenuItem.click()
  } else {
    repostButton?.click()
  }
}

async function deletePost(post: HTMLElement, isDry: boolean): Promise<void> {
  if (!post) {
    return
  }

  const menuButton = await getWaitForElementToAppearPromise(
    post,
    () =>
      evaluateXpath<HTMLElement>(
        post,
        'descendant::button[@aria-label="More"]',
      )[0],
    3000,
  )

  menuButton?.click()

  const menu = await getWaitForElementToAppearPromise(
    document,
    () => evaluateXpath<HTMLDivElement>(document, '//div[@role="menu"]')[0],
    5000,
  )

  if (!menu) {
    return
  }

  const deleteMenuItem = evaluateXpath<HTMLElement>(
    menu,
    '//div[@role="menuitem"]//*[contains(text(), "Delete")]',
  )[0]

  if (deleteMenuItem) {
    deleteMenuItem.click()
  }

  await wait(500)

  const confirmDeleteButton =
    await getWaitForElementToAppearPromise<HTMLButtonElement>(
      document,
      () =>
        evaluateXpath<HTMLButtonElement>(
          document,
          '//button[@role="button"]//*[contains(text(), "Delete")]',
        )[0],
      5000,
    )

  const cancelButton =
    await getWaitForElementToAppearPromise<HTMLButtonElement>(
      document,
      () =>
        evaluateXpath<HTMLButtonElement>(
          document,
          '//button[@role="button"]//*[contains(text(), "Cancel")]',
        )[0],
      5000,
    )

  if (confirmDeleteButton && !isDry) {
    confirmDeleteButton.click()
  } else {
    cancelButton?.click()
  }
}

async function removePostFromProfile(
  post: HTMLElement,
  isDry: boolean,
): Promise<void> {
  if (await isRepost(post)) {
    await undoRepost(post, isDry)
  } else {
    await deletePost(post, isDry)
  }
}

function setupPostMutationObserver(
  timeline: HTMLElement,
  postsToDelete: HTMLElement[],
): MutationObserver {
  const recurse = (el: HTMLElement) => {
    if (el.tagName === 'ARTICLE') {
      // grab date from post
      const dateElement = el.querySelector('time')
      const date = dateElement?.dateTime
      if (date) {
        remoteLog.debug(
          `setupPostMutationObserver.recurse: adding post to delete from date ${date}`,
        )
      } else {
        remoteLog.debug(
          `setupPostMutationObserver.recurse: adding post to delete from unknown date`,
        )
      }
      postsToDelete.push(el)
    }
    if (el.childNodes) {
      ;[...el.childNodes].forEach(recurse)
    }
  }

  const mutationObserver = new MutationObserver((mutationRecords) => {
    for (const mutationRecord of mutationRecords) {
      for (const node of mutationRecord.addedNodes) {
        recurse(node as HTMLElement)
      }
    }
  })
  mutationObserver.observe(timeline, {
    childList: true,
    subtree: true,
  })

  return mutationObserver
}

// Constructs the result object that should be saved to initialState storage
function buildResultObject(
  numPostsDeletedAllTime: number,
  numPostsDeletedThisTime: number,
  dateOption: OLDER_THAN_OPTION,
  selectedMonth: number,
  selectedYear: number,
  username: string,
): XDeletePostsState {
  const deletedDetail =
    `Your posts, replies, reposts, and quote posts` +
    `${dateOption !== OLDER_THAN_OPTION.NOW ? ` made before ${MONTHS[selectedMonth]} ${selectedYear}` : ''}` +
    ' have been deleted.'

  const noPostsFoundDetail =
    `${BuildConfig.TENANT_NAME} was not able to find any posts or replies` +
    `${dateOption !== OLDER_THAN_OPTION.NOW ? ` made before ${MONTHS[selectedMonth]} ${selectedYear}` : ''}` +
    ' on your profile.'

  return {
    count: numPostsDeletedAllTime,
    message: `Deleted ${numPostsDeletedAllTime} total posts.`,
    result: {
      title:
        numPostsDeletedThisTime > 0
          ? `You deleted ${numPostsDeletedThisTime} posts`
          : 'No posts deleted',
      detail: numPostsDeletedThisTime > 0 ? deletedDetail : noPostsFoundDetail,
      helper: {
        text: `Why weren't ${numPostsDeletedThisTime > 0 ? 'more' : 'any'} posts deleted? `,
        link: xRateLimitFaqUrl,
      },
    },
    username,
  }
}

async function setUpdateStageStatus(
  dateOption: string,
  olderThanMonth: number,
  olderThanYear: number,
  deletedCount: number,
): Promise<void> {
  const currentStatus = await getStoredMachineOverlayStatus(PLATFORM.TWITTER)

  const allPostsMsg = `${BuildConfig.TENANT_NAME} is deleting your posts...`
  const olderThanPostsMsg =
    `${BuildConfig.TENANT_NAME} is deleting posts you made before ` +
    `${MONTHS[olderThanMonth]} 1, ${olderThanYear}`

  await setStoredMachineOverlayStatus(PLATFORM.TWITTER, {
    ...currentStatus,
    title:
      dateOption !== OLDER_THAN_OPTION.NOW ? olderThanPostsMsg : allPostsMsg,
    deletedCount,
  })
}

function getUsernameFromUrl(): string | undefined {
  const currentUrl = new URL(window.location.href)
  const pattern = /\/(\w*)\/with_replies/gm
  const usernameMatch = pattern.exec(currentUrl.pathname)

  if (usernameMatch && usernameMatch.length > 1) {
    return usernameMatch[1]
  }

  return undefined
}

const StageKey = {
  GATHER_GO_TO_PROFILE: 'GATHER_GO_TO_PROFILE',
  GATHER_POST_INFORMATION: 'GATHER_POST_INFORMATION',
  UPDATE_GO_TO_PROFILE: 'UPDATE_GO_TO_PROFILE',
  UPDATE_DELETE_POSTS: 'UPDATE_DELETE_POSTS',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_GO_TO_PROFILE,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    urlPattern: 'https://x.com/.+',
    action: async () => {
      const profileUrl = await twitterUrls.getProfileUrl()
      window.location.assign(`${profileUrl}/with_replies`)
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_POST_INFORMATION,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    urlPattern: 'https://x.com/.+',
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      const timelineHeader =
        await getWaitForElementToAppearPromise<HTMLElement>(
          document,
          () =>
            evaluateXpath<HTMLElement>(
              document,
              '//div[@aria-label="Home timeline"]/*[1]',
            )[0],
        )

      if (timelineHeader) {
        const username = getUsernameFromUrl()

        const numPostsDiv = await getWaitForElementToAppearPromise<HTMLElement>(
          timelineHeader,
          (node) =>
            evaluateXpath<HTMLElement>(
              node,
              '//div[contains(text(), "post")]',
            )[0],
          3000,
        )

        let numPosts
        if (numPostsDiv) {
          numPosts = parseInt(numPostsDiv.textContent, 10)
        }

        const postDates = await getVisibleUserPostDates(username)
        postDates.sort((a, b) => Date.parse(b) - Date.parse(a))
        const mostRecentPostDate = postDates[0]

        await updateStoredInitialValues(
          {
            [XSettingKey.DELETE_POSTS_BEFORE]: {
              numPosts,
              mostRecentPostDate,
              username,
            },
          },
          TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE,
        )
      }
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_GO_TO_PROFILE,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: twitterUrls.authHomeUrl,
    urlPattern: 'https://x.com/.+',
    action: async (machineContext, userContext) => {
      const { clearStoredAutomationErrors, updateStoredAutomationErrors } =
        userContext
      await clearStoredAutomationErrors(
        TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE,
      )

      const profileUrl = await twitterUrls.getProfileUrl()
      if (!profileUrl) {
        sendAlertNotification(
          PLATFORM.TWITTER,
          'X: Delete all posts error',
          'Unable to find profile page to delete posts',
        )
        await updateStoredAutomationErrors(
          {
            [XSettingKey.DELETE_POSTS_BEFORE]: {
              message: 'Unable to find profile, failed to delete posts.',
            },
          },
          TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE,
        )
      }

      // Go to profile
      window.location.assign(`${profileUrl}/with_replies`)
      return true
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_DELETE_POSTS,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.FINDING_POSTS_TO_DELETE,
    timeout: NO_TIMEOUT,
    action: async (machineContext, userContext, additionalMachineData) => {
      const {
        incrementTotalCount,
        initialValues: storedInitialValues,
        isDry,
        updateStoredAutomationErrors,
        updateStoredInitialValues,
        settingsSelected,
      } = userContext
      const { resumeData } = additionalMachineData

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        XSettingKey.DELETE_POSTS_BEFORE,
      )

      const timeline = await getTimeline()
      if (!timeline) {
        sendAlertNotification(
          PLATFORM.TWITTER,
          'X: Delete all posts error',
          'Unable to load timeline to delete posts',
        )
        await updateStoredAutomationErrors(
          {
            [XSettingKey.DELETE_POSTS_BEFORE]: {
              message: 'Unable to load timeline, failed to delete posts.',
            },
          },
          TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE,
        )
        return
      }

      const selectedDateOption =
        settingsSelected?.[XSettingKey.DELETE_POSTS_BEFORE]?.dateOption ||
        OLDER_THAN_OPTION.NOW
      const username = initialValues?.username || getUsernameFromUrl()
      const [olderThanMonth, olderThanYear] = getOlderThanMonthYear(
        selectedDateOption,
        settingsSelected?.[XSettingKey.DELETE_POSTS_BEFORE]?.sinceDateMonth ||
          new Date().getMonth().toString(),
        settingsSelected?.[XSettingKey.DELETE_POSTS_BEFORE]?.sinceDateYear ||
          new Date().getFullYear().toString(),
      )

      const { foundPosts, foundNonRepost } = await scrollToEarliestPostAfter(
        selectedDateOption,
        olderThanMonth,
        olderThanYear,
        username,
      )

      if (!foundPosts) {
        if (foundNonRepost === false) {
          remoteLog.debug(
            `TWITTER_DELETE_POSTS_BEFORE action: only reposts found ` +
              `for date option ${selectedDateOption}, ` +
              `month ${olderThanMonth}, year ${olderThanYear}`,
          )
          await updateStoredInitialValues(
            {
              [XSettingKey.DELETE_POSTS_BEFORE]: {
                count: initialValues?.count || 0,
                message: `Deleted ${initialValues?.count || 0} total posts.`,
                result: {
                  title: 'No posts found',
                  detail:
                    `${BuildConfig.TENANT_NAME} only detected reposts on your profile. ` +
                    `Since reposts don't have a visible timestamp, ` +
                    `only the "All posts" option can be used for deletion.`,
                },
                username,
              },
            },
            TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE,
          )
        } else {
          remoteLog.debug(
            `TWITTER_DELETE_POSTS_BEFORE action: no posts found ` +
              ` for date option ${selectedDateOption}, ` +
              `month ${olderThanMonth}, year ${olderThanYear}`,
          )
          await updateStoredInitialValues(
            {
              [XSettingKey.DELETE_POSTS_BEFORE]: buildResultObject(
                initialValues?.count || 0,
                0,
                selectedDateOption,
                olderThanMonth,
                olderThanYear,
                username,
              ),
            },
            TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE,
          )
        }
      } else {
        remoteLog.debug(
          `TWITTER_DELETE_POSTS_BEFORE action: deleting posts for ${selectedDateOption}, ` +
            `month ${olderThanMonth}, year ${olderThanYear}`,
        )
        const nextPostsToDelete = await getAllPostsLaterThan(
          selectedDateOption,
          olderThanMonth,
          olderThanYear,
          username,
        )

        let totalPostCount = initialValues?.count || 0
        let currentPostCount = resumeData?.data?.deletedCount || 0
        let numTryNoChange = 0

        const observer = setupPostMutationObserver(timeline, nextPostsToDelete)

        try {
          while (nextPostsToDelete.length > 0 || numTryNoChange < 3) {
            if (nextPostsToDelete.length === 0) {
              numTryNoChange += 1
              window.scrollBy(0, window.innerHeight)
              await wait(1000)
            } else {
              numTryNoChange = 0

              const post = nextPostsToDelete.pop()
              await removePostFromProfile(post, isDry)

              totalPostCount += 1
              currentPostCount += 1

              await incrementTotalCount(
                TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE,
              )

              await setUpdateStageStatus(
                selectedDateOption,
                olderThanMonth,
                olderThanYear,
                currentPostCount,
              )

              await resumeData.set({
                ...resumeData.data,
                deletedCount: currentPostCount,
              })

              await updateStoredInitialValues(
                {
                  [XSettingKey.DELETE_POSTS_BEFORE]: buildResultObject(
                    totalPostCount,
                    currentPostCount,
                    selectedDateOption,
                    olderThanMonth,
                    olderThanYear,
                    username,
                  ),
                },
                TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE,
              )

              await userContext.updateStatisticsLastCount(
                TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE,
                currentPostCount,
              )
            }
          }
        } finally {
          observer.disconnect()
        }
      }
    },
  },
]
const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
